import {
  faCalendar,
  faCalendarCheck,
  faCalendarXmark,
  faLock,
} from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Divider } from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { BaseSelectOption } from 'src/shared/Forms/Selects/BaseSelect';
import { ServiceSelect } from 'src/shared/Forms/Selects/ServiceSelect';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';

import { EditEventDialog } from './EditEventDialog';
import { UnavailableDialog } from './UnavailableDialog';

import type { Appointment } from 'src/models/appointment/_types';
import type { ScheduleItem } from 'src/models/schedule/_types';
import type { Identifier } from 'src/types/utilityTypes';

interface NewEventDialogProps {
  open: boolean;
  onClose: () => void;
  date?: Date | null;
  trainerId?: Identifier;
  /** Availability schedule to show edit option for (when clicking on an availability event) */
  availabilitySchedule?: ScheduleItem | null;
  onCreateUnavailable?: (date: Date, trainerId?: Identifier) => void;
  onSaveSuccess?: (appointment: Appointment) => void;
  onScheduleSaveSuccess?: (schedule: ScheduleItem) => void;
}

export const NewEventDialog: FC<NewEventDialogProps> = ({
  open,
  onClose,
  date,
  trainerId,
  availabilitySchedule,
  onCreateUnavailable,
  onSaveSuccess,
  onScheduleSaveSuccess,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'schedules.calendar.newEvent',
  });
  const [selectedServiceId, setSelectedServiceId] = useState<Identifier | null>(
    null
  );
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUnavailableDialog, setShowUnavailableDialog] = useState(false);
  const [showEditAvailabilityDialog, setShowEditAvailabilityDialog] =
    useState(false);

  const { canEditSchedules } = usePermissionCheck();

  const handleServiceSelect = (value: Identifier | BaseSelectOption | null) => {
    const serviceId = typeof value === 'object' ? value?.id : value;
    setSelectedServiceId(serviceId ?? null);

    if (!serviceId || !date) return;

    // Open the EditEventDialog with the selected service
    setShowEditDialog(true);
  };

  const handleCreateUnavailable = () => {
    if (!date) return;

    if (onCreateUnavailable) {
      onCreateUnavailable(date, trainerId);
      handleClose();
    } else {
      // Open the CreateUnavailableDialog
      setShowUnavailableDialog(true);
    }
  };

  const handleClose = () => {
    setSelectedServiceId(null);
    setShowEditDialog(false);
    setShowUnavailableDialog(false);
    setShowEditAvailabilityDialog(false);
    onClose();
  };

  const handleEditAvailability = () => {
    setShowEditAvailabilityDialog(true);
  };

  const handleEditAvailabilityDialogClose = () => {
    setShowEditAvailabilityDialog(false);
  };

  const handleEditAvailabilitySaveSuccess = (schedule: ScheduleItem) => {
    handleClose();
    onScheduleSaveSuccess?.(schedule);
  };

  const handleEditDialogClose = () => {
    setShowEditDialog(false);
    setSelectedServiceId(null);
  };

  const handleEditDialogSaveSuccess = (appointment: Appointment) => {
    handleClose();
    onSaveSuccess?.(appointment);
  };

  const handleUnavailableDialogClose = () => {
    setShowUnavailableDialog(false);
  };

  const handleUnavailableSaveSuccess = (schedule: ScheduleItem) => {
    handleClose();
    onScheduleSaveSuccess?.(schedule);
  };

  const formattedDate = date ? format(date, "EEEE, MMMM do 'at' h:mm a") : '';

  // If editing availability schedule, show that dialog
  if (showEditAvailabilityDialog && availabilitySchedule) {
    return (
      <UnavailableDialog
        open={showEditAvailabilityDialog}
        onClose={handleEditAvailabilityDialogClose}
        schedule={availabilitySchedule}
        onSaveSuccess={handleEditAvailabilitySaveSuccess}
        onDeleteSuccess={handleClose}
      />
    );
  }

  // If EditEventDialog is open, only render that
  if (showEditDialog) {
    return (
      <EditEventDialog
        open={showEditDialog}
        onClose={handleEditDialogClose}
        initialDate={date}
        initialServiceId={selectedServiceId}
        initialTrainerId={trainerId}
        onSaveSuccess={handleEditDialogSaveSuccess}
      />
    );
  }

  // If UnavailableDialog is open, only render that (create mode - no schedule passed)
  if (showUnavailableDialog) {
    return (
      <UnavailableDialog
        open={showUnavailableDialog}
        onClose={handleUnavailableDialogClose}
        initialDate={date}
        initialTrainerId={trainerId}
        onSaveSuccess={handleUnavailableSaveSuccess}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('title')}
      maxWidth="xs"
      fullWidth
    >
      {canEditSchedules ? (
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 2,
              borderRadius: 1,
              bgcolor: 'background.level1',
            }}
          >
            <Icon icon={faCalendar} color="primary" />
            <Typography variant="body1" fontWeight="medium">
              {formattedDate}
            </Typography>
          </Box>

          {availabilitySchedule && (
            <>
              <Button
                color="neutral"
                variant="bordered"
                fullWidth
                onClick={handleEditAvailability}
                startIcon={<Icon icon={faCalendarCheck} />}
              >
                {t('editAvailabilitySchedule')}
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography color="text.secondary" variant="caption">
                  {t('orCreateNewEvent')}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </>
          )}

          <ServiceSelect
            label={t('createSession')}
            value={selectedServiceId}
            onChange={(v) => handleServiceSelect(v)}
            placeholder={t('selectService')}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography color="text.secondary" variant="caption">
              {t('or')}
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          <Button
            color="neutral"
            variant="bordered"
            fullWidth
            onClick={handleCreateUnavailable}
            startIcon={<Icon icon={faCalendarXmark} />}
          >
            {t('blockTimeAsUnavailable')}
          </Button>
        </Stack>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            px: 2,
          }}
        >
          <Icon icon={faLock} size="large" color="disabled" sx={{ mb: 2 }} />
          <Typography color="text.secondary">{t('noPermission')}</Typography>
        </Box>
      )}
    </Dialog>
  );
};
